
import { Pricing } from "@/api/dtos/Pricing.schema";
import { deepClone } from "@/misc/helpers";
import { deviceTypeAndPropertiesToGerman } from "@/misc/i18n";
import { Component, Prop, Vue } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import EditFrame from "../EditFrame.vue";
@Component({
  components: {
    EditFrame,
    DangerButton,
    CancelButton,
  },
})
export default class StationPricing extends Vue {
  deviceTypeAndPropertiesToGerman = deviceTypeAndPropertiesToGerman;

  @Prop({
    default: [],
  })
  devicePrices!: Pricing[];

  @Prop({
    default: false,
  })
  readonly hasFinanceData!: boolean;

  selectedPricingModel: Pricing | null = null;
  showEditDialog = false;

  get minuteActive(): boolean {
    return this.selectedPricingModel?.pricePerMinuteEuros !== null;
  }

  set minuteActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerMinuteEuros = checked ? 1 : null;
    }
  }

  get hourActive(): boolean {
    return this.selectedPricingModel?.pricePerHourEuros !== null;
  }

  set hourActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerHourEuros = checked ? 1 : null;
    }
  }

  get dayActive(): boolean {
    return this.selectedPricingModel?.pricePerDayEuros !== null;
  }

  set dayActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerDayEuros = checked ? 1 : null;
    }
  }

  get weekActive(): boolean {
    return this.selectedPricingModel?.pricePerWeekEuros !== null;
  }

  set weekActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerWeekEuros = checked ? 1 : null;
    }
  }

  get monthActive(): boolean {
    return this.selectedPricingModel?.pricePerMonthEuros !== null;
  }

  set monthActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerMonthEuros = checked ? 1 : null;
    }
  }

  get yearActive(): boolean {
    return this.selectedPricingModel?.pricePerYearEuros !== null;
  }

  set yearActive(checked: boolean) {
    if (this.selectedPricingModel) {
      this.selectedPricingModel.pricePerYearEuros = checked ? 1 : null;
    }
  }
  get isFreePricing(): boolean {
    if (!this.selectedPricingModel) return false;
    const p = this.selectedPricingModel;
    return [
      p.pricePerMinuteEuros,
      p.pricePerHourEuros,
      p.pricePerDayEuros,
      p.pricePerWeekEuros,
      p.pricePerMonthEuros,
      p.pricePerYearEuros,
    ].every((v) => v == null);
  }

  calculatePrice(minutes: number): {
    total: number;
    breakdown: { [key: string]: number };
  } {
    if (!this.selectedPricingModel) {
      return { total: 0, breakdown: {} };
    }

    const {
      pricePerMinuteEuros: priceMinutes,
      pricePerHourEuros: priceHours,
      pricePerDayEuros: priceDays,
      pricePerWeekEuros: priceWeeks,
      pricePerMonthEuros: priceMonths,
      pricePerYearEuros: priceYears,
    } = this.selectedPricingModel;

    const intervals = [
      { name: "Jahr", duration: 525600, price: priceYears },
      { name: "Monat", duration: 43200, price: priceMonths },
      { name: "Woche", duration: 10080, price: priceWeeks },
      { name: "Tag", duration: 1440, price: priceDays },
      { name: "Stunde", duration: 60, price: priceHours },
      { name: "Minute", duration: 1, price: priceMinutes },
    ].filter((interval) => interval.price != null);

    // Nothing active: free
    if (intervals.length === 0) {
      return { total: 0, breakdown: {} };
    }

    let totalCost = 0;
    const breakdown: { [key: string]: number } = {};
    let remainingMinutes = minutes;

    for (const interval of intervals) {
      if (remainingMinutes < interval.duration || !interval.price) continue;

      const count = Math.floor(remainingMinutes / interval.duration);
      totalCost += count * interval.price;
      breakdown[interval.name] = (breakdown[interval.name] || 0) + count;
      remainingMinutes -= count * interval.duration;
    }

    // Charge the smallest active tier if there is leftover time
    if (remainingMinutes > 0) {
      const smallestInterval = intervals[intervals.length - 1];
      breakdown[smallestInterval.name] =
        (breakdown[smallestInterval.name] || 0) + 1;
      totalCost += smallestInterval.price!;
    }

    return { total: totalCost, breakdown };
  }

  displayPrice(price: {
    total: number;
    breakdown: { [key: string]: number };
  }): string {
    let output = `${price.total.toFixed(2).replace(".", ",")} EUR - (`;
    const entries = Object.entries(price.breakdown);

    entries.forEach((entry, index) => {
      const [key, value] = entry;
      output += `${value} x ${key}`;
      if (index < entries.length - 1) {
        output += ", ";
      }
    });

    output += ")";
    return output;
  }

  editPricingModel(pricingModel: Pricing): void {
    this.selectedPricingModel = deepClone(pricingModel);
    this.showEditDialog = true;
  }

  onEditDone(): void {
    this.showEditDialog = false;
    this.$emit("save", this.selectedPricingModel);
  }

  isFree(model: Pricing | null | undefined): boolean {
    if (!model) return false;
    const {
      pricePerMinuteEuros,
      pricePerHourEuros,
      pricePerDayEuros,
      pricePerWeekEuros,
      pricePerMonthEuros,
      pricePerYearEuros,
    } = model;
    return [
      pricePerMinuteEuros,
      pricePerHourEuros,
      pricePerDayEuros,
      pricePerWeekEuros,
      pricePerMonthEuros,
      pricePerYearEuros,
    ].every((v) => v == null);
  }

  readonly headers: {
    text: string;
    value: keyof Pricing | "actions";
    align?: "start" | "center" | "end";
  }[] = [
    {
      text: "Typ",
      value: "deviceType",
    },
    {
      text: "EUR/Minute",
      value: "pricePerMinuteEuros",
    },
    {
      text: "EUR/Stunde",
      value: "pricePerHourEuros",
    },
    {
      text: "EUR/Tag",
      value: "pricePerDayEuros",
    },
    {
      text: "EUR/Woche",
      value: "pricePerWeekEuros",
    },
    {
      text: "EUR/Monat",
      value: "pricePerMonthEuros",
    },
    {
      text: "EUR/Jahr",
      value: "pricePerYearEuros",
    },
    {
      text: "",
      value: "actions",
    },
  ];
}
